import { useEffect, useState } from "react";

import {
  getFavoriteCustomerId,
  toggleFavoriteCustomerId
} from "common/customer/getFavoriteCustomer";
import { Icon } from "components/icon/Icon";
import MenuDropdown from "components/menu/MenuDropdown/MenuDropdown";
import { useCustomerId } from "hooks/use-customer-id";
import { useRootStore } from "store";

import "./MenuDropdown.scss";

type Props = {
  handleChangeCustomer: (customerId: string) => void;
};

export default function ExternalUserCustomerMenu({
  handleChangeCustomer
}: Props) {
  const customerId = useCustomerId();
  const { unfilteredCustomers: customers } = useRootStore(
    (state) => state.customers.myCustomers
  );
  const { user } = useRootStore((state) => state);
  const [search, setSearch] = useState("");
  const [favoriteCustomerId, setFavoriteCustomerId] = useState<string | null>(
    null
  );

  useEffect(() => {
    setFavoriteCustomerId(getFavoriteCustomerId(user.userInfo?.id));
  }, [user.userInfo?.id]);

  const customersItems = customers
    .filter(
      (c) =>
        search === "" ||
        c.customerName.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => (a.customerName > b.customerName ? 1 : -1));

  const setFavoriteCustomer = (
    e: React.MouseEvent<HTMLSpanElement, MouseEvent>,
    customerNumber: string
  ) => {
    e.stopPropagation();
    const newState = toggleFavoriteCustomerId(
      user.userInfo?.id,
      customerNumber
    );
    setFavoriteCustomerId(newState ? customerNumber : null);
  };

  const searchMenuItem = {
    content: (
      <div className="customerSearchWrapper">
        <input
          placeholder="Sök verksamhet..."
          type="text"
          value={search}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
    ),
    hasDivider: true,
    className: "p-0"
  };

  const customersMenuItems = customersItems.map((customer) => ({
    content: (
      <div className="d-flex justify-content-between">
        <span className={customerId === customer.customerId ? "fw-bold" : ""}>
          {customer.customerName}
        </span>
        <span
          className={
            favoriteCustomerId === customer.customerNumber
              ? "favoriteActive"
              : "favorite"
          }
          title={
            favoriteCustomerId === customer.customerNumber
              ? `Ta bort som favorit`
              : `Lägg till som favorit`
          }
          onClick={(e) => setFavoriteCustomer(e, customer.customerNumber)}
        >
          <Icon
            name={
              favoriteCustomerId === customer.customerNumber ? "star" : "star-o"
            }
          />
        </span>
      </div>
    ),
    onClick: () => handleChangeCustomer(customer.customerId)
  }));

  const hasMultipleOperations = customers.length >= 5;

  const handleMenuToggle = (isOpen: boolean) => {
    // Reset search when menu is collapsed
    if (!isOpen) {
      setSearch("");
    }
  };

  const allMenuItems = [
    ...(hasMultipleOperations ? [searchMenuItem] : []),
    ...customersMenuItems,
    ...(search !== "" && customersItems.length === 0
      ? [
          {
            content: <p>Inga sökresultat på "{search}".</p>
          }
        ]
      : [])
  ];

  return (
    <>
      <div className="customerDropdown">
        <MenuDropdown
          toggleText="Mina verksamheter"
          menuItems={allMenuItems}
          onToggle={handleMenuToggle}
        />
      </div>

      <div className="customerMenuFullscreen">
        <h2 className="mobile-menu-header">Mina verksamheter </h2>
        {hasMultipleOperations && (
          <div className="customerSearchWrapper customerSearchWrapperRounded">
            <input
              placeholder="Sök verksamhet..."
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        )}
        <ul className="list-unstyled">
          {customersMenuItems.map((customerItem, i) => (
            <li key={i} className="py-2">
              <a onClick={customerItem.onClick}>{customerItem.content}</a>
            </li>
          ))}

          {search !== "" && customersItems.length === 0 && (
            <p>Inga sökresultat på "{search}".</p>
          )}
        </ul>
      </div>
    </>
  );
}
